import { useState, useRef, useEffect } from 'react'
import { ChevronDown, Check } from 'lucide-react'
import { cn } from '../utils/cn'

export default function CustomDropdown({ 
  options = [], 
  value, 
  onChange, 
  placeholder = "Select option",
  className = "",
  disabled = false 
}) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Close dropdown on escape key
  useEffect(() => {
    function handleEscape(event) {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      return () => document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen])

  const selectedOption = options.find(option => option.value === value)

  const handleSelect = (option) => {
    onChange(option.value)
    setIsOpen(false)
  }

  return (
    <div className={cn("relative", className)} ref={dropdownRef}>
      {/* Dropdown Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={cn(
          "w-full px-4 py-3 text-left bg-white/80 backdrop-blur-sm border border-gray-300 rounded-2xl shadow-sm transition-all duration-200",
          "focus:ring-2 focus:ring-blue-500 focus:border-transparent",
          "hover:bg-white/90 hover:shadow-md",
          disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer",
          isOpen && "ring-2 ring-blue-500 border-transparent shadow-lg"
        )}
      >
        <div className="flex items-center justify-between">
          <span className={cn(
            "font-medium",
            selectedOption ? "text-gray-900" : "text-gray-500"
          )}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <ChevronDown 
            className={cn(
              "h-4 w-4 text-gray-400 transition-transform duration-200",
              isOpen && "transform rotate-180"
            )} 
          />
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-2 bg-white/95 backdrop-blur-md border border-white/50 rounded-2xl shadow-2xl overflow-hidden">
          <div className="py-2 max-h-60 overflow-y-auto">
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleSelect(option)}
                className={cn(
                  "w-full px-4 py-3 text-left text-sm transition-all duration-150",
                  "hover:bg-gradient-to-r hover:from-blue-50 hover:to-cyan-50",
                  option.value === value 
                    ? "bg-gradient-to-r from-blue-50 to-cyan-50 text-blue-700 font-semibold" 
                    : "text-gray-700 hover:text-blue-700"
                )}
              >
                <div className="flex items-center justify-between">
                  <span>{option.label}</span>
                  {option.value === value && (
                    <Check className="h-4 w-4 text-blue-600" />
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
} 